import styled, { css } from 'styled-components';
import { Box, Flex } from '@lemonade-hq/cdk';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis';
import { Section, MainHeader, SectionParagraph } from '@/components/shared/Content';
import VimeoLightbox from './VimeoLightBox';
import floorImg from '@/static/assets/notion/floor.png';
import armChairImg from '@/static/assets/notion/bg-living-room-mobile.svg';
import JoinCTA from '../shared/JoinCTA';

/* eslint-disable indent */
const sharedPseudoBG = css`
  content: '';
  position: absolute;
  width: 550px;
  height: 100%;
  background-image: url(${(props: BGImgProps) => props.bgImg.img});
  background-position: ${(props: BGImgProps) => props.bgImg.position};
  background-repeat: no-repeat;
  bottom: 0;
`;

const sharedBeforeBG = css`
  ${sharedPseudoBG};
  background-position: left bottom;
`;

const sharedAfterBG = css`
  ${sharedPseudoBG};
  background-position: right bottom;
`;

/* eslint-disable indent */
const TopFoldSection = styled(Section)`
  position: relative;
  background-image: url(${props => props.bgImg.img});
  background-size: ${props => props.bgImg.size};
  background-repeat: no-repeat;
  background-position: ${({ bgImg }) => bgImg.position};
  background-color: transparent;
  z-index: 1;

  ${MEDIA_QUERIES.upToDesktop`
        background-position-x: calc(50% + 123px);
        background-image: url(${armChairImg.src});
        background-size: 1253px 500px;
        height: 100%;
    `};

  ${MEDIA_QUERIES.desktop`
        background-position: center bottom;
        background-image: url(${props => props.bgImg.img});
        height: 678px;
        background-size: auto;
    `};

  @media (min-width: 1500px) {
    background-image: url(${floorImg.src});
    background-repeat: repeat-x;
    background-size: 17px 789px;
    height: 678px;
    &:before {
      ${sharedBeforeBG};
      left: 0;
    }
    &:after {
      ${sharedAfterBG};
      right: 0;
    }
  }
`;
/* eslint-enable indent */

const TopFoldTitle = styled(MainHeader)`
  ${MEDIA_QUERIES.tabletAndDesktop`
        white-space: pre;
        z-index: 1;
    `}

  ${MEDIA_QUERIES.mobile`
        padding: 0 43px 0 43px;
    `}

    @media (max-width: 1024px) and (min-width: 575px) {
    font-size: 2.8rem;
  }
`;

const TopFoldSubtitle = styled(SectionParagraph)`
  ${MEDIA_QUERIES.tabletAndDesktop`
        white-space: pre;
        z-index: 1;
    `}

  ${MEDIA_QUERIES.mobile`
        padding: 0 20px 0 20px;
    `}
`;

const Actions = styled(Flex)`
  z-index: 1;
`;

const CTA = styled(JoinCTA)`
  height: 47px;
  padding: 15px 62px;

  &&& {
    display: block;
    font-size: 1.4rem;
    width: 232px;
  }
`;

export type BGImgProps = {
  bgImg: BGImg;
};

export interface BGImg {
  img: string;
  size: string;
  position: string;
}

export interface HeroProps {
  title: string;
  subtitle: string;
  bgImg: BGImg;
  videoId: number;
  videoCta: string;
  cta: string;
  ctaUrl?: string;
}

export const Hero = ({ title, subtitle, bgImg, videoId, videoCta, cta }: HeroProps) => {
  return (
    <TopFoldSection px={[20, 0]} pt={[15, 27, 15]} pb={[535, 535, 73]} bgImg={bgImg}>
      <TopFoldTitle>{title}</TopFoldTitle>
      <Box mt={[15, 20]} style={{ zIndex: 1 }}>
        <TopFoldSubtitle>{subtitle}</TopFoldSubtitle>
      </Box>
      <Actions flexDirection="column" alignItems="center">
        <Box mt={[30, 45, 50]}>
          <CTA flavor="primary" glowing id="gtm_button_main_check_prices" data-source="top">
            {cta}
          </CTA>
        </Box>
        <Box mt={24} ml={-2}>
          <VimeoLightbox {...{ videoId }} text={videoCta} gtmId="gtm_link_watch_the_video_above_the_fold" />
        </Box>
      </Actions>
    </TopFoldSection>
  );
};
